import { AlertType } from '../../types/alerts';
import { Brain, BookOpen, GitBranch, Wand2 } from 'lucide-react';

interface AlertTypeSelectorProps {
  selectedType: AlertType | null | 'automatic';
  onTypeChange: (type: AlertType | null | 'automatic') => void;
  showAutomatic?: boolean;
}

const alertTypes: { type: AlertType; icon: typeof Brain; label: string; color: string }[] = [
  { type: 'behaviour', icon: Brain, label: 'Behaviour', color: 'text-purple-500' },
  { type: 'rule', icon: BookOpen, label: 'Rule', color: 'text-blue-500' },
  { type: 'sequential', icon: GitBranch, label: 'Sequential', color: 'text-primary' },
];

const createAlertTypes: { type: AlertType | 'automatic'; icon: typeof Brain; label: string; color: string }[] = [
  { type: 'automatic', icon: Wand2, label: 'Automatic', color: 'text-amber-500' },
  { type: 'behaviour', icon: Brain, label: 'Behaviour', color: 'text-purple-500' },
  { type: 'rule', icon: BookOpen, label: 'Rule', color: 'text-blue-500' },
  { type: 'sequential', icon: GitBranch, label: 'Sequential', color: 'text-primary' },
];

export function AlertTypeSelector({ selectedType, onTypeChange, showAutomatic = false }: AlertTypeSelectorProps) {
  const types = showAutomatic ? createAlertTypes : alertTypes;
  
  const handleClick = (type: AlertType | 'automatic') => {
    // For automatic mode, don't allow toggle off
    if (showAutomatic) {
      onTypeChange(type);
      return;
    }
    // Toggle: if already selected, unselect (null means show all)
    if (selectedType === type) {
      onTypeChange(null);
    } else {
      onTypeChange(type);
    }
  };

  return (
    <div className="flex items-center justify-center gap-0.5 p-1 bg-card rounded-lg border border-border shadow-sm h-9">
      {types.map((alertType) => {
        const Icon = alertType.icon;
        const isSelected = selectedType === alertType.type;
        return (
          <button
            key={alertType.type}
            onClick={() => handleClick(alertType.type)}
            className={`
              flex items-center justify-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-all min-w-[90px] h-7
              ${isSelected 
                ? 'bg-background text-foreground shadow-md ring-1 ring-primary/30' 
                : 'text-muted-foreground hover:text-foreground hover:bg-background/60'
              }
            `}
          >
            <Icon 
              className={`h-3.5 w-3.5 transition-all duration-300 ${
                isSelected ? alertType.color : 'opacity-60'
              }`}
            />
            <span>{alertType.label}</span>
          </button>
        );
      })}
    </div>
  );
}
